// /src/pages/BecomePartner.jsx
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  FaHandshake,
  FaClipboardCheck,
  FaShieldAlt,
  FaFileSignature,
  FaGraduationCap,
  FaUserShield,
  FaBuilding,
  FaCheckCircle,
  FaArrowRight,
  FaInfoCircle,
  FaListUl,
  FaExternalLinkAlt,
} from "react-icons/fa";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

export default function BecomePartner() {
  const checklistItems = [
    { id: "license", label: "I hold an active resident insurance license (or I’m getting licensed now)." },
    { id: "background", label: "I can pass standard background checks required by carriers." },
    { id: "eo", label: "I have current E&O (Errors & Omissions) coverage or will obtain it." },
    { id: "business", label: "I have a business entity or plan to operate as an individual producer." },
    { id: "compliance", label: "I agree to follow all CMS and carrier compliance guidelines." },
    // Removed W-9 / ACH item per request
  ];

  const [checked, setChecked] = useState({});
  const allChecked =
    checklistItems.length > 0 &&
    checklistItems.every((item) => checked[item.id]);

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <>
      <Helmet>
        <title>How to Become a Partner | Your Choice Associates</title>
        <meta
          name="description"
          content="Join Your Choice Associates as a broker partner. Review the checklist, get licensed, and complete simple onboarding steps to start receiving leads."
        />
        <link rel="canonical" href="https://yourchoiceassociates.org/become-a-partner" />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#002b5c] to-[#001b35] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <motion.div {...fade} className="space-y-5">
            <span className="inline-block text-xs font-semibold tracking-wider uppercase bg-white/10 px-3 py-1 rounded-full">
              Partner Program
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              How to Become a Partner
            </h1>
            <p className="text-white/90">
              Join a network focused on compliant growth, real support, and quality leads.
              Follow the steps below—starting with licensing—and we’ll help you get appointed and ready.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-white text-[#002b5c] px-3 py-1 rounded-full text-sm font-semibold">
                Medicare & Life Focused
              </span>
              <span className="bg-white/10 px-3 py-1 rounded-full text-sm font-semibold">
                Training & Support
              </span>
            </div>
          </motion.div>

          <motion.div {...fade} className="bg-white/5 rounded-2xl p-6 md:p-8">
            <ul className="space-y-4">
              {[
                { icon: <FaHandshake />, t: "Collaborative team culture" },
                { icon: <FaUserShield />, t: "Compliance-first processes" },
                { icon: <FaBuilding />, t: "Carrier relationships" },
                { icon: <FaListUl />, t: "Simple onboarding steps" },
              ].map((i, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="mt-1">{i.icon}</div>
                  <p>{i.t}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Pre-application Checklist */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-3">
            Checklist before you apply
          </motion.h2>
          <p className="text-gray-600 mb-8 max-w-2xl">
            Please confirm the items below. Once everything is checked, you can start your application.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div {...fade} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-[#002b5c] font-semibold mb-4">
                <FaClipboardCheck /> Readiness Items
              </div>
              <div className="space-y-3">
                {checklistItems.map((item) => (
                  <label key={item.id} className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300"
                      checked={!!checked[item.id]}
                      onChange={() => toggle(item.id)}
                    />
                    <span className="text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {Object.values(checked).filter(Boolean).length}/{checklistItems.length} completed
                </div>
                <Link
                  to={allChecked ? "/partner-apply" : "#"}
                  onClick={(e) => {
                    if (!allChecked) e.preventDefault();
                  }}
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition ${
                    allChecked
                      ? "bg-[#002b5c] text-white hover:opacity-90"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Apply Now <FaArrowRight />
                </Link>
              </div>

              {!allChecked && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                  <FaInfoCircle /> Complete all items to enable the Apply button.
                </div>
              )}
            </motion.div>

            {/* Step 1: Get Licensed */}
            <motion.div {...fade} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 text-[#002b5c] font-semibold mb-2">
                <FaGraduationCap /> Step 1: Get Licensed
              </div>
              <p className="text-gray-700">
                If you're not yet licensed, you’ll need to complete your insurance certification.
                We recommend <strong>XCEL Solutions</strong> to get started. Their courses are fast,
                affordable, and approved in most states.
              </p>
              <a
                href="https://www.xcelsolutions.com/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl border font-semibold hover:bg-gray-50"
              >
                Visit XCEL Solutions <FaExternalLinkAlt className="text-xs" />
              </a>

              <div className="mt-6 grid gap-4">
                <div className="rounded-xl border p-4">
                  <div className="font-semibold">What you’ll do</div>
                  <ul className="list-disc pl-5 text-gray-700 space-y-1 mt-2">
                    <li>Pick your state & complete pre-licensing education</li>
                    <li>Schedule & pass your state exam</li>
                    <li>Submit fingerprints/background (where required)</li>
                    <li>Apply for your resident producer license</li>
                  </ul>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="font-semibold">Tip</div>
                  <p className="text-gray-700">
                    Save your completion certificate and license number—you’ll need them during onboarding.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Onboarding Timeline */}
      <section className="py-16 md:py-24 bg-[#f7fafc]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <motion.h2 {...fade} className="text-2xl md:text-4xl font-bold mb-10">
            Simple onboarding steps
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <FaShieldAlt />,
                title: "Get E&O Coverage",
                desc: "Submit Errors & Omissions (E&O) insurance—many carriers require it.",
              },
              {
                icon: <FaFileSignature />,
                title: "Complete Onboarding Forms",
                desc: "Carrier-required forms and portal setup (no banking or tax forms required here).",
              },
              {
                icon: <FaUserShield />,
                title: "Carrier Appointments",
                desc: "We’ll guide you through getting appointed with the right carriers.",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.05 }}
                className="bg-white rounded-2xl p-6 shadow-sm border"
              >
                <div className="text-2xl">{step.icon}</div>
                <h3 className="mt-3 font-bold text-lg">{step.title}</h3>
                <p className="mt-2 text-gray-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 bg-white rounded-2xl p-6 shadow-sm border">
            <div className="flex items-center gap-2 font-semibold text-[#002b5c]">
              <FaInfoCircle /> After approval
            </div>
            <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-3">
              <li>Access to our broker portal and resources</li>
              <li>Lead alerts and support for compliant enrollments</li>
              <li>Ongoing training and product updates</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#002b5c] text-white rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold">Ready to partner with us?</h3>
              <p className="mt-2 text-white/90">
                Confirm the checklist and start your application in minutes.
              </p>
            </div>
            <Link
              to={allChecked ? "/partner-apply" : "#"}
              onClick={(e) => {
                if (!allChecked) e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition ${
                allChecked
                  ? "bg-white text-[#002b5c] hover:opacity-90"
                  : "bg-white/20 text-white/70 cursor-not-allowed"
              }`}
            >
              {allChecked ? "Start Application" : "Complete Checklist First"} <FaArrowRight />
            </Link>
          </div>
          {!allChecked && (
            <div className="mt-3 text-sm text-gray-600 flex items-center gap-2">
              <FaInfoCircle /> You can still explore onboarding steps while you finish requirements.
            </div>
          )}
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#002b5c] font-semibold">
            <FaCheckCircle /> Compliant growth • Carrier relationships • Agent support
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
